<template>
  <div class="consultation-linked-page">
    <div class="profile-card">
      <div v-if="detailLoading" class="loading-block">
        <van-loading type="spinner" />
      </div>
      <template v-else>
        <div class="profile-header">
          <div class="profile-avatar">{{ profileInitial }}</div>
          <div class="profile-main">
            <div class="profile-name-row">
              <div class="profile-name">{{ profileName }}</div>
              <div class="profile-badge">{{ statusText }}</div>
            </div>
            <div class="profile-subtitle">{{ profileSubtitle }}</div>
          </div>
        </div>

        <div class="profile-meta">
          <div
            v-if="detail?.phone || detail?.otherConcatMethods || props.target?.phone || props.target?.otherConcatMethods"
            class="meta-item"
          >
            <div class="meta-label">联系方式</div>
            <div class="meta-value">{{ detail?.phone || detail?.otherConcatMethods || props.target?.phone || props.target?.otherConcatMethods }}</div>
          </div>
          <div v-if="getDictLabel(consultation_area, detail?.area)" class="meta-item">
            <div class="meta-label">区域</div>
            <div class="meta-value">{{ getDictLabel(consultation_area, detail?.area) }}</div>
          </div>
          <div v-if="getDictLabel(grade_list, detail?.grade ?? props.target?.grade)" class="meta-item">
            <div class="meta-label">年级</div>
            <div class="meta-value">{{ getDictLabel(grade_list, detail?.grade ?? props.target?.grade) }}</div>
          </div>
          <div v-if="detail?.nearbySchoolName || props.target?.schoolName" class="meta-item">
            <div class="meta-label">就近校区</div>
            <div class="meta-value">{{ detail?.nearbySchoolName || props.target?.schoolName }}</div>
          </div>
          <div v-if="getDictLabel(consultation_source, detail?.source)" class="meta-item">
            <div class="meta-label">来源</div>
            <div class="meta-value">{{ getDictLabel(consultation_source, detail?.source) }}</div>
          </div>
          <div v-if="getDictLabel(student_type, detail?.studentType)" class="meta-item">
            <div class="meta-label">学生类型</div>
            <div class="meta-value">{{ getDictLabel(student_type, detail?.studentType) }}</div>
          </div>
          <div v-if="getDictLabel(common_level, detail?.clueQuality)" class="meta-item">
            <div class="meta-label">线索质量</div>
            <div class="meta-value">{{ getDictLabel(common_level, detail?.clueQuality) }}</div>
          </div>
          <div v-if="detail?.followUserName || props.target?.ownerName" class="meta-item">
            <div class="meta-label">跟进人</div>
            <div class="meta-value">{{ detail?.followUserName || props.target?.ownerName }}</div>
          </div>
          <div v-if="formatMultiDict(course_subject, detail?.intentionSubject)" class="meta-item">
            <div class="meta-label">意向科目</div>
            <div class="meta-value">{{ formatMultiDict(course_subject, detail?.intentionSubject) }}</div>
          </div>
          <div v-if="formatMultiDict(service_type, detail?.services)" class="meta-item">
            <div class="meta-label">意向服务</div>
            <div class="meta-value">{{ formatMultiDict(service_type, detail?.services) }}</div>
          </div>
          <div v-if="detail?.remark" class="meta-item meta-item-wide">
            <div class="meta-label">备注</div>
            <div class="meta-value">{{ detail?.remark }}</div>
          </div>
        </div>
      </template>
    </div>

    <div class="record-card">
      <div class="section-header">
        <div class="section-title">跟进记录</div>
        <button
          v-if="consultationBookId"
          type="button"
          class="section-action"
          @click="goToAddFollowRecord"
        >
          +
        </button>
      </div>
      <div v-if="recordLoading" class="loading-block">
        <van-loading type="spinner" />
      </div>
      <div v-else-if="records.length" class="record-list">
        <div v-for="record in records" :key="record.consultationRecordId" class="record-item">
          <div class="record-line"></div>
          <div class="record-dot"></div>
          <div class="record-body">
            <div class="record-head">
              <div class="record-user">{{ record.followUserName || '未知跟进人' }}</div>
              <div class="record-time">{{ formatDateTime(record.followTime) }}</div>
            </div>
            <div class="record-tags">
              <span v-if="record.followType" class="record-tag">
                {{ getDictLabel(follow_up_method, record.followType) }}
              </span>
              <span v-if="record.followStage" class="record-tag muted">
                {{ getDictLabel(follow_up_phase, record.followStage) }}
              </span>
            </div>
            <div class="record-content">{{ record.followContent || '-' }}</div>
            <div v-if="splitAnnexUrls(record.annex).length" class="record-images">
              <img
                v-for="(url, index) in splitAnnexUrls(record.annex)"
                :key="`${record.consultationRecordId}-${index}`"
                :src="url"
                class="record-image"
                @click="openPreview(record.annex, index)"
              />
            </div>
          </div>
        </div>
      </div>
      <div v-else class="empty-block">暂无跟进记录</div>
    </div>

    <van-image-preview
      v-model:show="showPreview"
      :images="previewImages"
      :start-position="previewStartIndex"
      :show-index="true"
      :closeable="true"
    />
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { showToast } from 'vant'
import { useDict } from '@/utils/dict'
import { getConsultationBookDetail, getConsultationRecords } from '@/api/crm'

const props = defineProps({
  target: {
    type: Object,
    default: null
  }
})

const {
  consultation_area,
  consultation_source,
  consultation_status,
  grade_list,
  student_type,
  common_level,
  course_subject,
  service_type,
  follow_up_method,
  follow_up_phase
} = useDict(
  'consultation_area',
  'consultation_source',
  'consultation_status',
  'grade_list',
  'student_type',
  'common_level',
  'course_subject',
  'service_type',
  'follow_up_method',
  'follow_up_phase'
)

const detailLoading = ref(false)
const recordLoading = ref(false)
const detail = ref(null)
const records = ref([])
const showPreview = ref(false)
const previewImages = ref([])
const previewStartIndex = ref(0)
const router = useRouter()
const pageScrollLockState = {
  htmlOverflow: '',
  htmlOverscrollBehavior: '',
  bodyOverflow: '',
  bodyOverscrollBehavior: ''
}

const consultationBookId = computed(() => props.target?.targetId || detail.value?.consultationBookId || '')
const profileName = computed(() => detail.value?.contactName || props.target?.name || '-')
const profileInitial = computed(() => (profileName.value || '-').slice(0, 1))
const statusText = computed(() => getDictLabel(consultation_status.value, detail.value?.status ?? props.target?.status) || '未知状态')
const profileSubtitle = computed(() => {
  const parts = [
    getDictLabel(grade_list.value, detail.value?.grade ?? props.target?.grade),
    detail.value?.nearbySchoolName || props.target?.schoolName
  ].filter(Boolean)
  return parts.join(' / ') || '线索档案'
})

onMounted(() => {
  setPageScrollLocked(true)
})

onBeforeUnmount(() => {
  setPageScrollLocked(false)
})

watch(() => props.target?.targetId, async consultationBookId => {
  detail.value = null
  records.value = []
  previewImages.value = []
  previewStartIndex.value = 0
  showPreview.value = false
  if (!consultationBookId) {
    return
  }
  detailLoading.value = true
  recordLoading.value = true
  try {
    const [detailResult, result] = await Promise.all([
      getConsultationBookDetail(consultationBookId),
      getConsultationRecords(consultationBookId)
    ])
    detail.value = detailResult
    records.value = Array.isArray(result) ? result : []
  } catch (error) {
    showToast(error?.message || '加载线索信息失败')
  } finally {
    detailLoading.value = false
    recordLoading.value = false
  }
}, { immediate: true })

function getDictLabel(list, value) {
  if (value === undefined || value === null || value === '') {
    return ''
  }
  const matched = list.find(item => String(item.dictValue) === String(value))
  return matched ? matched.dictLabel : String(value)
}

function setPageScrollLocked(locked) {
  if (typeof document === 'undefined') {
    return
  }

  const { documentElement, body } = document
  if (locked) {
    pageScrollLockState.htmlOverflow = documentElement.style.overflow
    pageScrollLockState.htmlOverscrollBehavior = documentElement.style.overscrollBehavior
    pageScrollLockState.bodyOverflow = body.style.overflow
    pageScrollLockState.bodyOverscrollBehavior = body.style.overscrollBehavior
    documentElement.style.overflow = 'hidden'
    documentElement.style.overscrollBehavior = 'none'
    body.style.overflow = 'hidden'
    body.style.overscrollBehavior = 'none'
    return
  }

  documentElement.style.overflow = pageScrollLockState.htmlOverflow
  documentElement.style.overscrollBehavior = pageScrollLockState.htmlOverscrollBehavior
  body.style.overflow = pageScrollLockState.bodyOverflow
  body.style.overscrollBehavior = pageScrollLockState.bodyOverscrollBehavior
}

function formatDateTime(value) {
  if (!value) {
    return '-'
  }
  return new Date(value).toLocaleString('zh-CN', { hour12: false })
}

function formatMultiDict(list, value) {
  if (!value) {
    return ''
  }
  return String(value)
    .split(',')
    .map(item => getDictLabel(list, item))
    .filter(Boolean)
    .join('、')
}

function splitAnnexUrls(annexStr) {
  if (!annexStr) {
    return []
  }
  const cleanedStr = String(annexStr).replace(/^[\s"`]+|[\s"`]+$/g, '')
  return cleanedStr.split(/[,\n]/).map(item => item.trim()).filter(Boolean)
}

function openPreview(annexStr, index) {
  previewImages.value = splitAnnexUrls(annexStr)
  previewStartIndex.value = index
  showPreview.value = true
}

function goToAddFollowRecord() {
  router.push({
    path: '/consultationBook/addFollowRecord',
    query: { consultationBookId: consultationBookId.value }
  })
}
</script>

<style scoped>
.consultation-linked-page {
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: calc(100vh - 48px);
  min-height: 0;
  box-sizing: border-box;
}

.profile-card,
.record-card {
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.04);
  overflow: hidden;
}

.profile-card {
  padding: 16px;
  flex-shrink: 0;
}

.profile-header {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 18px;
}

.profile-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #8b5cf6 0%, #c084fc 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  font-weight: 700;
  flex-shrink: 0;
}

.profile-main {
  min-width: 0;
  flex: 1;
}

.profile-name-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
  flex-wrap: wrap;
}

.profile-name {
  font-size: 22px;
  font-weight: 700;
  color: #111827;
}

.profile-badge {
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  color: #7c3aed;
  background: #f3e8ff;
}

.profile-subtitle {
  font-size: 13px;
  color: #94a3b8;
}

.profile-meta {
  display: flex;
  flex-wrap: wrap;
  column-gap: 12px;
  row-gap: 6px;
  margin-top: 10px;
}

.meta-item {
  flex: 0 0 calc(50% - 6px);
  min-width: calc(50% - 6px);
  padding: 2px 0;
  line-height: 1.4;
}

.meta-item-wide {
  flex-basis: 100%;
  min-width: 100%;
}

.meta-label {
  display: inline;
  font-size: 13px;
  color: #777;
  line-height: 1.4;
}

.meta-label::after {
  content: '：';
}

.meta-value {
  display: inline;
  font-size: 13px;
  font-weight: 500;
  color: #222;
  line-height: 1.4;
  word-break: break-word;
}

.record-card {
  flex: 1;
  min-height: 0;
  padding-bottom: 10px;
  display: flex;
  flex-direction: column;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 16px 0;
  margin-bottom: 8px;
}

.section-title {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

.section-action {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 999px;
  padding: 0;
  font-size: 20px;
  line-height: 28px;
  color: #2563eb;
  background: rgba(37, 99, 235, 0.1);
}

.loading-block,
.empty-block {
  min-height: 120px;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 14px;
  padding: 0 16px;
}

.record-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-y: contain;
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 0 16px 4px;
}

.record-item {
  position: relative;
  padding: 0 0 16px 24px;
}

.record-item:last-child {
  padding-bottom: 0;
}

.record-line {
  position: absolute;
  left: 8px;
  top: 0;
  bottom: 0;
  width: 1px;
  background: rgba(191, 219, 254, 0.92);
}

.record-item:last-child .record-line {
  bottom: 14px;
}

.record-dot {
  position: absolute;
  left: 3px;
  top: 6px;
  width: 11px;
  height: 11px;
  border-radius: 999px;
  background: #2563eb;
  box-shadow: 0 0 0 3px rgba(219, 234, 254, 0.9);
}

.record-body {
  padding: 14px 14px 14px 16px;
  border-radius: 14px;
  background: rgba(248, 250, 252, 0.92);
}

.record-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.record-user {
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}

.record-time {
  font-size: 13px;
  color: #94a3b8;
  white-space: nowrap;
}

.record-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.record-tag {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  background: #edf3ff;
  color: #2563eb;
  font-size: 12px;
}

.record-tag.muted {
  background: #f4f6fb;
  color: #475569;
}

.record-content {
  margin-top: 12px;
  font-size: 14px;
  line-height: 1.7;
  color: #334155;
  white-space: pre-wrap;
  word-break: break-word;
}

.record-images {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 12px;
}

.record-image {
  width: 76px;
  height: 76px;
  border-radius: 10px;
  object-fit: cover;
  background: #e5e7eb;
}

</style>
